import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography, Paper, useTheme } from '@mui/material';
import { StatusMessage } from '../../contexts/LoadingStatusContext';

interface LoadingIndicatorProps {
  statuses?: StatusMessage[];
  singleton?: boolean; // Optional prop to control singleton behavior
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({ statuses = [], singleton = false }) => {
  const theme = useTheme();

  useEffect(() => {
    if (statuses.length > 0) {
      console.log('DEBUG: LoadingIndicator: Active statuses:', statuses.map(s => ({id: s.id, message: s.message})));
    } else {
      console.log('DEBUG: LoadingIndicator: No active statuses');
    }
  }, [statuses]);

  if (!singleton || statuses.length === 0) {
    console.log('LoadingIndicator: No active statuses to display or singleton mode is off');
    return null; // Don't render anything if no statuses
  }

  console.log('LoadingIndicator: Rendering with statuses:', statuses);
  const dedup_status_bymessage = statuses.reduce((acc: StatusMessage[], status: StatusMessage) => {
    const existingStatus = acc.find(s => s.message === status.message);
    if (!existingStatus) {
      acc.push(status); // Add unique status
    } else {
      console.log(`LoadingIndicator: Duplicate status found, not adding: ${status.message}`);
    }
    return acc;
  }, []);

  return (
    <Box 
      sx={{ 
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1200,
        maxWidth: '80%',
        maxHeight: '30%',
        overflowY: 'auto',
      }}
    >
      {dedup_status_bymessage.map((status) => (
        <Paper 
          key={status.id} 
          elevation={2} 
          sx={{ 
            p: 0.75,
            px: 1.5, 
            mb: 1, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.85)' 
              : 'rgba(255, 255, 255, 0.85)', 
            backdropFilter: 'blur(5px)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5
          }}
        > 
          <CircularProgress 
            color="primary" 
            size={16} 
            thickness={5} 
            sx={{ opacity: 0.8 }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.7)' 
                : 'rgba(0, 0, 0, 0.6)',
              fontSize: '0.8rem',
              fontWeight: 400
            }}
          >
            {status.message}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default LoadingIndicator;
